import React, { useEffect, useReducer } from 'react';

import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { MdErrorOutline, MdOutlineCloudUpload as UploadIcon } from 'react-icons/md';
import { BsFillXCircleFill as RemoveIcon } from 'react-icons/bs';

import API from 'utils/api';
import { useSources, useValueChangeEffect } from 'hooks';
import { Progress } from 'components';
import { call, classes } from 'utils';

import styles from './Media.module.scss';

const initialState = props => {
  const { mediaFile, mediaUrl, contentType, localMediaFile } = props;

  return {
    mediaFile: mediaFile || null,
    mediaUrl: (mediaFile && mediaUrl) || null,
    contentType: (mediaFile && contentType) || null,
    localMediaFile: localMediaFile || null,
    status: 'init',
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return state.status !== 'loading' ? { ...state, status: 'loading' } : state;

    case 'SET_DATA': {
      const newState = {
        ...state,
        ..._.omitBy(
          _.pick(action.payload, ['mediaFile', 'mediaUrl', 'contentType']),
          _.isUndefined
        ),
        status: _.get(action, 'payload.error') ? 'error' : 'active',
      };

      return _.isEqual(state, newState) ? state : newState;
    }

    case 'SET_LOCAL_FILE': {
      if (action.payload !== state.localMediaFile) {
        if (state.localMediaUrl) {
          URL.revokeObjectURL(state.localMediaUrl);
        }

        return {
          ...state,
          localMediaFile: action.payload,
          localMediaUrl: action.payload ? URL.createObjectURL(action.payload) : null,
        };
      }

      return state;
    }

    case 'CLEAR_DATA': {
      if (state.localMediaUrl) {
        URL.revokeObjectURL(state.localMediaUrl);
      }

      const newState = {
        mediaFile: null,
        mediaUrl: null,
        contentType: null,
        localMediaFile: null,
        localMediaUrl: null,
        status: 'active',
      };

      return _.isEqual(state, newState) ? state : newState;
    }

    default:
      return state;
  }
};

const Media = props => {
  const [state, dispatch] = useReducer(reducer, props, initialState);
  const sources = useSources();

  useEffect(() => {
    if (props.mediaFile) {
      if (props.mediaUrl) {
        dispatch({
          type: 'SET_DATA',
          payload: { mediaFile: props.mediaFile, mediaUrl: props.mediaUrl },
        });
      } else {
        dispatch({ type: 'LOADING' });

        API.getFileUrl(props.mediaFile, sources).then(({ data, error }) =>
          dispatch({
            type: 'SET_DATA',
            payload: { mediaFile: props.mediaFile, mediaUrl: data, error },
          })
        );
      }
    } else if (props.localMediaFile) {
      dispatch({ type: 'SET_LOCAL_FILE', payload: props.localMediaFile });
    } else {
      dispatch({ type: 'CLEAR_DATA' });
    }
  }, [sources, props.mediaFile, props.mediaUrl, props.localMediaFile]);

  useValueChangeEffect(state, changes =>
    call(
      props.onChange,
      _.pick(changes, ['mediaFile', 'mediaUrl', 'contentType', 'localMediaFile'])
    )
  );

  const { status, mediaFile, mediaUrl, localMediaFile, localMediaUrl } = state;
  const { autoUpload = true, mediaType = 'image' } = props;
  const useLocal = !autoUpload && mediaType !== 'video';

  switch (status) {
    case 'active':
      break;

    case 'loading':
      return (
        <div className={styles.media} style={props.style}>
          <Progress />
        </div>
      );

    case 'error':
      return (
        <div className={classes(styles.media, styles.error)} style={props.style}>
          <MdErrorOutline />
        </div>
      );

    default:
      return null;
  }

  const onInput = files => {
    if (status === 'active' && Array.isArray(files) && files.length) {
      if (useLocal) {
        dispatch({ type: 'SET_LOCAL_FILE', payload: files[0] });
      } else {
        dispatch({ type: 'LOADING' });

        const params = _.omitBy({ id: mediaFile, tag: props.mediaTag }, _.isNil);
        let executor, endpoint;

        switch (props.encoding) {
          case 'multipart':
            executor = API.uploadFileMultipart;
            endpoint = props.endpoint || '/template/file';
            break;

          default:
            executor = API.uploadFileBase64;
            endpoint = props.endpoint || '/template/base64';
            break;
        }

        executor(endpoint, files[0], params, sources).then(({ data, error }) => {
          dispatch({
            type: 'SET_DATA',
            payload: data
              ? {
                  mediaFile: data.id,
                  mediaUrl: data.url,
                  contentType: data.contentType || null,
                }
              : { error },
          });
        });
      }
    }
  };

  const onDelete = () => {
    if (status === 'active') {
      if (useLocal) {
        dispatch({ type: 'CLEAR_DATA' });
      } else if (mediaFile) {
        dispatch({ type: 'LOADING' });

        API.deleteFile('/template/file', mediaFile, sources).then(({ status }) =>
          dispatch({ type: status === 202 || status === 404 ? 'CLEAR_DATA' : 'SET_DATA' })
        );
      }
    }
  };

  const scale = typeof props.scale === 'number' ? props.scale || 100 : 100;

  const dropzone = accept => (
    <Dropzone
      className={styles.dropzone}
      accept={accept}
      onDrop={onInput}
      maxSize={props.maxSize || 10 * 1024 * 1024}
    >
      <UploadIcon className={styles.icon} style={{ fontSize: scale * 0.15 }} />

      {mediaFile || localMediaFile ? (
        <div
          className={styles.btRemove}
          onClick={event => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <RemoveIcon />
        </div>
      ) : null}
    </Dropzone>
  );

  if (mediaType === 'video') {
    return (
      <div className={classes(styles.media, styles.video)} style={props.style}>
        {mediaUrl ? (
          <video autoPlay loop muted>
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : null}

        <div style={{ padding: scale * 0.0618 }}>{dropzone('video/mp4')}</div>
      </div>
    );
  }

  const url = localMediaUrl || mediaUrl;

  return (
    <div
      className={classes(styles.media, styles.image)}
      style={_.merge(
        {
          backgroundImage: url ? `url(${url})` : undefined,
          padding: scale * 0.0618,
        },
        props.style
      )}
    >
      {dropzone('image/jpeg, image/jpg, image/png, image/gif')}
    </div>
  );
};

export default Media;
